import { MarkedCellType, type MarkedCell, type Tutorial, type TutorialStep } from '@puzzlio/core';
import { EDGE_DOWN, EDGE_RIGHT, type CellValue, type Grid, type LoopyRiverBoard } from './types';
import {
  boardHasSingleContinuousLoop,
  checkEdgeBlocked,
  countCells,
  riverPassesThroughInOrder,
} from './logic/checkBoard';

type Coord = [row: number, col: number];
type EdgeBlock = { cells: [Coord, Coord]; edge: number };

const mark = (row: number, col: number, markType: MarkedCellType): MarkedCell => ({ row, col, markType });
const reference = ([row, col]: Coord) => mark(row, col, MarkedCellType.reference);

/** Highlight where a segment has to grow from, and point at the cell it grows into. */
const extendSegment = ([from, to]: Coord[]) => [mark(from[0], from[1], MarkedCellType.highlight), reference(to)];

const blockEdge = ({ cells: [cell, neighbour], edge }: EdgeBlock) => [
  mark(cell[0], cell[1], edge === EDGE_DOWN ? MarkedCellType.edgeDown : MarkedCellType.edgeRight),
  reference(neighbour),
];

/** Lock every cell but the ones marked. */
const withDisabled = (grid: Grid, marks: MarkedCell[]): MarkedCell[] => {
  const marked = new Set(marks.map((m) => `${m.row},${m.col}`));
  const disabled: MarkedCell[] = [];
  grid.forEach((row, r) =>
    row.forEach((_, c) => {
      if (!marked.has(`${r},${c}`)) disabled.push(mark(r, c, MarkedCellType.disabled));
    })
  );
  return [...marks, ...disabled];
};

/** A read-only interlude between lessons. */
const interlude = (heading: string, text: string, nextLabel: string, canInteract: boolean) => (): TutorialStep => ({
  message: { kind: 'instruction', heading, blocks: [text], done: true, nextLabel, showReset: false },
  canInteract,
});

/** A lesson with one thing to do, ticked off in a checklist. The board locks once it's done. */
const lesson = (text: string, task: string, done: boolean, marks: MarkedCell[]): TutorialStep => ({
  message: { kind: 'instruction', blocks: [text, { kind: 'tasks', tasks: [{ text: task, done }] }], done },
  marks,
  canInteract: !done,
});

/**
 * A lesson made of several targets, walked one at a time: only the first unfinished target is marked
 * and unlocked, so the player is never looking at more than one thing.
 */
const oneAtATime =
  <T>(
    text: string,
    task: string,
    targets: T[],
    isDone: (target: T, cells: CellValue[][]) => boolean,
    marksFor: (target: T) => MarkedCell[]
  ) =>
  ({ board, cells }: { board: LoopyRiverBoard; cells: CellValue[][] }): TutorialStep => {
    const next = targets.find((target) => !isDone(target, cells));
    return lesson(text, task, next === undefined, next === undefined ? [] : withDisabled(board.grid, marksFor(next)));
  };

const passesThrough = (coords: Coord[], cells: CellValue[][]) => riverPassesThroughInOrder(cells, coords);
const edgeBlocked = ({ cells: [[row, col]], edge }: EdgeBlock, cells: CellValue[][]) =>
  checkEdgeBlocked(cells, row, col, edge);

const UPPER_RIGHT: Coord[] = [
  [0, 3],
  [0, 4],
  [1, 4],
];
const UPPER_LEFT: Coord[] = [
  [0, 1],
  [0, 0],
  [1, 0],
];
const LOWER_LEFT: Coord[] = [
  [4, 0],
  [5, 0],
  [5, 1],
];
const LOWER_RIGHT: Coord[] = [
  [5, 3],
  [5, 4],
  [4, 4],
];
const CANYON: Coord[] = [
  [3, 2],
  [3, 3],
  [3, 4],
];

/*
  Two boards: `tutorial-0` is a free-form warm-up, and the lessons from step 2 on are written against
  the coordinates of `tutorial-1`.
*/
export const tutorial: Tutorial<LoopyRiverBoard, CellValue> = {
  game: 'loopy-river',
  phases: [
    { slug: 'tutorial-0', firstStep: 0 },
    { slug: 'tutorial-1', firstStep: 2 },
  ],
  steps: [
    ({ board, cells, complete }) => ({
      message: {
        kind: 'instruction',
        heading: 'Welcome to Loopy River!',
        blocks: [
          'Your goal is to draw a single loop that flows through every cell.',
          {
            kind: 'tasks',
            tasks: [
              { text: 'Flow through all cells.', done: countCells(cells, board.grid).empty === 0 },
              { text: 'Make a single complete loop.', done: boardHasSingleContinuousLoop(board.grid, cells) },
            ],
          },
          complete ? 'You did it! 🎉' : 'Tap and drag to draw the river.',
        ],
        done: complete,
      },
    }),

    interlude(
      'Move on Down the River',
      "You've got the basics! Now, let's learn to solve some trickier puzzles.",
      "Let's Go!",
      true
    ),

    ({ board, cells }) =>
      lesson(
        'Start with the corners. A corner has only one possible shape.',
        'Draw a river through these three cells.',
        passesThrough(UPPER_RIGHT, cells),
        withDisabled(board.grid, UPPER_RIGHT.map(reference))
      ),

    ({ board, cells }) => {
      const corners = [UPPER_LEFT, UPPER_RIGHT, LOWER_LEFT, LOWER_RIGHT];
      return lesson(
        'Now fill in the other three corners.',
        'Fill in all the corners.',
        corners.every((corner) => passesThrough(corner, cells)),
        withDisabled(board.grid, corners.flat().map(reference))
      );
    },

    oneAtATime(
      'Now look for interior corners. Same logic here, away from the edges.',
      'Fill in the interior corners.',
      [
        [
          [1, 2],
          [1, 3],
          [2, 3],
        ],
        [
          [5, 1],
          [5, 2],
          [4, 2],
        ],
        [
          [4, 0],
          [3, 0],
          [3, 1],
        ],
      ] as Coord[][],
      passesThrough,
      (coords) => coords.map(reference)
    ),

    interlude('Turn the Corner', 'No more corners! Time for a new strategy.', 'Tell Me More', false),

    oneAtATime(
      'Extend any segments that can only grow in one direction.',
      'Extend all segments that only have one exit.',
      [
        [
          [1, 0],
          [1, 1],
        ],
        [
          [0, 3],
          [0, 2],
        ],
        [
          [1, 4],
          [2, 4],
        ],
        [
          [5, 3],
          [4, 3],
        ],
      ] as Coord[][],
      passesThrough,
      extendSegment
    ),

    ({ board, cells }) =>
      lesson(
        'Two parallel walls create a canyon, and the river can only flow straight through.',
        'Flow through the canyon.',
        passesThrough(CANYON, cells),
        withDisabled(board.grid, CANYON.map(reference))
      ),

    oneAtATime(
      'The river has to be one continuous loop. Mini-loops are not allowed. Tap an edge to put an X on it.',
      'Block edges that would form mini-loops.',
      [
        {
          cells: [
            [0, 1],
            [1, 1],
          ],
          edge: EDGE_DOWN,
        },
        {
          cells: [
            [4, 3],
            [4, 4],
          ],
          edge: EDGE_RIGHT,
        },
      ] as EdgeBlock[],
      edgeBlocked,
      blockEdge
    ),

    interlude(
      'Stay the Course',
      "Our river is looking pretty crazy, but we promise it's all going to turn into one beautiful loop soon!",
      'OK...',
      false
    ),

    oneAtATime(
      'Now we have more cells that can only grow in one direction.',
      'Extend the new one-exit cells.',
      [
        [
          [0, 1],
          [0, 2],
        ],
        [
          [4, 4],
          [3, 4],
        ],
        [
          [2, 4],
          [2, 3],
        ],
        [
          [4, 3],
          [4, 2],
        ],
      ] as Coord[][],
      passesThrough,
      extendSegment
    ),

    oneAtATime(
      'Now there are two new mini-loops that we can block.',
      'Block edges that would form mini-loops.',
      [
        {
          cells: [
            [1, 1],
            [1, 2],
          ],
          edge: EDGE_RIGHT,
        },
        {
          cells: [
            [3, 1],
            [3, 2],
          ],
          edge: EDGE_RIGHT,
        },
      ] as EdgeBlock[],
      edgeBlocked,
      blockEdge
    ),

    ({ complete }) => ({
      message: {
        kind: 'instruction',
        heading: 'Bring it Home!',
        blocks: [
          "Extend those final cells and the river is done! You got this. We're not even going to give you any hints! 😅",
          { kind: 'tasks', tasks: [{ text: 'Extend the one-exit cells.', done: complete }] },
        ],
        done: complete,
        nextLabel: complete ? 'Tada!' : undefined,
      },
    }),

    () => ({
      message: {
        kind: 'complete',
        title: 'Tutorial Complete! 🎉🎉',
        description:
          "What a beautiful river! You're off to a great start. Now put those skills to use on some real puzzles.",
      },
    }),
  ],
};
